import { useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { Button } from '@/design-system/Button'
import { apiClient } from '@/lib/apiClient'

/**
 * La photo du candidat, envoyée depuis le formulaire public.
 *
 * Le champ existait côté API depuis toujours ; aucun moyen de le remplir
 * n'existait, l'envoi de fichier étant réservé au back-office. Les candidats
 * envoyaient donc leur photo par WhatsApp, et quelqu'un la remettait à la main.
 *
 * **L'aperçu est carré**, comme une photo d'identité 4×4. Ce n'est pas
 * décoratif : c'est ainsi qu'elle apparaîtra sur le site et sur le bulletin de
 * vote, et découvrir après coup qu'on est coupé au menton fait renvoyer la
 * photo. Le cadrage se fait à l'affichage — le fichier d'origine n'est jamais
 * modifié, pour que l'équipe puisse le réutiliser autrement.
 *
 * L'envoi se fait **avant** la soumission du formulaire : une photo refusée
 * après avoir tout rempli ferait tout ressaisir.
 */
export function PhotoField({
  slug,
  value,
  onChange,
}: {
  slug: string
  value: { media_id: string; url: string } | null
  onChange: (photo: { media_id: string; url: string } | null) => void
}) {
  const { t } = useTranslation()
  const input = useRef<HTMLInputElement>(null)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  async function upload(file: File) {
    setBusy(true)
    setError(null)
    try {
      const body = new FormData()
      body.append('file', file)
      const result = await apiClient.post<{ media_id: string; url: string }>(
        `/public/events/${slug}/competition/photo`,
        body,
      )
      onChange(result)
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : t('apply.photoFailed'))
    } finally {
      setBusy(false)
    }
  }

  return (
    <div>
      <p className="text-caption text-content-muted">{t('apply.photo')}</p>
      <p className="mt-1 text-caption text-content-muted">{t('apply.photoHint')}</p>

      <div className="mt-3 flex flex-wrap items-start gap-4">
        <div className="h-28 w-28 shrink-0 overflow-hidden rounded-md border border-line bg-bg-overlay">
          {value ? (
            <img src={value.url} alt="" className="h-full w-full object-cover" />
          ) : (
            <div className="flex h-full items-center justify-center text-caption text-content-muted">
              4 × 4
            </div>
          )}
        </div>

        <div className="flex flex-col gap-2">
          <input
            ref={input}
            type="file"
            accept="image/*"
            className="hidden"
            onChange={(event) => {
              const file = event.target.files?.[0]
              if (file) void upload(file)
              // Remis à zéro : sans cela, renvoyer le même fichier après un
              // échec ne déclencherait aucun évènement.
              event.target.value = ''
            }}
          />
          <Button
            type="button"
            variant="outline"
            size="sm"
            disabled={busy}
            onClick={() => input.current?.click()}
          >
            {busy
              ? t('apply.photoSending')
              : value
                ? t('apply.photoReplace')
                : t('apply.photoChoose')}
          </Button>
          {value ? (
            <Button type="button" variant="ghost" size="sm" onClick={() => onChange(null)}>
              {t('apply.photoRemove')}
            </Button>
          ) : null}
        </div>
      </div>

      {error ? <p className="mt-2 text-sm text-ember-300">{error}</p> : null}
    </div>
  )
}
